// Quién entró (su cuenta: correo y contraseña, Google, Apple o Facebook; o, con el celular prendido, su teléfono), sus
// negocios y las reglas de la tienda (`GET /tienda/yo`). El perfil queda guardado para abrir sin señal; el negocio
// elegido viaja como `cliente_id` en todas las llamadas. Una cuenta nueva viene "falta completar" y pasa por el registro
// (decisiones D48 y D51).
import { create } from 'zustand';
import { borrar, guardar, leer } from './almacen';
import { api, ErrorApi, mensajeDeError, SinRed } from './api';
import { abrirCarrito } from './estado-carrito';
import { aplicarMarca, useMarca } from './marca';
import { fijarSesion, type Sesion, useSesion } from './sesion';
import { claveLocal, SLUG } from './tienda-actual';
import { NOMBRE_PROVEEDOR, nuevoDesafio, type Proveedor, tomarSecreto } from './google';
import type { Negocio, Perfil, RespuestaEntrada, RespuestaGoogle, Tokens } from './tipos';

/** Resultado de entrar con correo y contraseña o con el enlace para elegirla: con negocio o a completar los datos. */
export type RespuestaCuenta = RespuestaEntrada & { estado: 'listo' | 'falta_completar' };

const CLAVE_PERFIL = claveLocal('perfil');
const CLAVE_NEGOCIO = claveLocal('negocio');

type EstadoPerfil = 'sin_cargar' | 'cargando' | 'listo' | 'error' | 'bloqueado';

interface EstadoCuenta {
  perfil: Perfil | null;
  estado: EstadoPerfil;
  /** Mensaje del error o del bloqueo (tienda cerrada, número sin acceso). */
  mensaje: string | null;
  /** El perfil que se muestra es el guardado: no hubo señal para traer el de ahora. */
  sinRed: boolean;
  negocioId: string | null;
}

type Quien = { telefono: string | null; identidad_id?: string | null; vendedor?: { id: string } | null };

/** La misma persona: el mismo teléfono, la misma cuenta de Google o el mismo preventista (decisión D60). */
export function mismaCuenta(a: Quien | null, b: Quien | null): boolean {
  if (!a || !b) return false;
  if (a.vendedor || b.vendedor) return !!a.vendedor && a.vendedor.id === b.vendedor?.id;
  return a.telefono ? a.telefono === b.telefono : !!a.identidad_id && a.identidad_id === b.identidad_id;
}

function perfilGuardado(): Perfil | null {
  const p = leer<Perfil>(CLAVE_PERFIL);
  return mismaCuenta(p, useSesion.getState().sesion) ? p : null;
}

const inicial = perfilGuardado();
export const useCuenta = create<EstadoCuenta>(() => ({
  perfil: inicial,
  estado: inicial ? 'listo' : 'sin_cargar',
  mensaje: null,
  sinRed: false,
  negocioId: leer<string>(CLAVE_NEGOCIO),
}));

/** El negocio con que se pide: el elegido o, si hay uno solo, ese. */
export function negocioDe(perfil: Perfil | null, elegido: string | null): Negocio | null {
  if (!perfil) return null;
  if (perfil.clientes.length === 1) return perfil.clientes[0]!;
  return perfil.clientes.find((c) => c.id === elegido) ?? null;
}

export const useNegocio = (): Negocio | null => useCuenta((s) => negocioDe(s.perfil, s.negocioId));

export function elegirNegocio(id: string | null): void {
  if (id) guardar(CLAVE_NEGOCIO, id);
  else borrar(CLAVE_NEGOCIO);
  useCuenta.setState({ negocioId: id });
  abrirCarrito(negocioDe(useCuenta.getState().perfil, id)?.id ?? null);
}

let cargando: Promise<void> | null = null;

export function cargarPerfil(): Promise<void> {
  cargando ??= cargarAhora().finally(() => {
    cargando = null;
  });
  return cargando;
}

async function cargarAhora(): Promise<void> {
  if (!useCuenta.getState().perfil) useCuenta.setState({ estado: 'cargando', mensaje: null });
  try {
    const p = await api<Perfil>('/tienda/yo');
    guardar(CLAVE_PERFIL, p);
    const { negocioId } = useCuenta.getState();
    // El negocio elegido ya no es de este teléfono: se vuelve a elegir (o queda el único).
    const sigue = p.clientes.some((c) => c.id === negocioId);
    useCuenta.setState({
      perfil: p,
      estado: 'listo',
      mensaje: null,
      sinRed: false,
      negocioId: sigue ? negocioId : null,
    });
    if (!sigue) borrar(CLAVE_NEGOCIO);
    abrirCarrito(negocioDe(p, sigue ? negocioId : null)?.id ?? null);
    if (p.marca) {
      // La marca puede haber cambiado desde que se abrió (color, nombre): se aplica la de ahora.
      const actual = useMarca.getState().marca;
      if (actual && actual.version !== p.marca.version) useMarca.setState({ marca: { ...actual, ...p.marca } });
      aplicarMarca(p.marca);
    }
  } catch (e) {
    if (e instanceof ErrorApi && e.estado === 403) {
      useCuenta.setState({ estado: 'bloqueado', mensaje: e.message, sinRed: false });
      return;
    }
    if (e instanceof ErrorApi && e.estado === 401) return; // la sesión se cerró: la guardia lleva a entrar
    const conGuardado = !!useCuenta.getState().perfil;
    if (conGuardado && e instanceof SinRed) {
      useCuenta.setState({ estado: 'listo', sinRed: true });
      abrirCarrito(negocioDe(useCuenta.getState().perfil, useCuenta.getState().negocioId)?.id ?? null);
      return;
    }
    useCuenta.setState({
      estado: conGuardado ? 'listo' : 'error',
      mensaje: mensajeDeError(e),
      sinRed: e instanceof SinRed,
    });
  }
}

// ---------- Entrar y salir ----------

/** "Chrome en Android": lo que ve la distribuidora en la lista de sesiones. */
function nombreDispositivo(): string {
  const ua = typeof navigator === 'undefined' ? '' : navigator.userAgent;
  const so = /android/i.test(ua)
    ? 'Android'
    : /iphone|ipad/i.test(ua)
      ? 'iPhone'
      : /windows/i.test(ua)
        ? 'Windows'
        : /mac os/i.test(ua)
          ? 'Mac'
          : 'Web';
  const nav = /samsungbrowser/i.test(ua)
    ? 'Samsung Internet'
    : /edg\//i.test(ua)
      ? 'Edge'
      : /firefox|fxios/i.test(ua)
        ? 'Firefox'
        : /crios|chrome/i.test(ua)
          ? 'Chrome'
          : /safari/i.test(ua)
            ? 'Safari'
            : 'Navegador';
  return `Tienda · ${nav} en ${so}`;
}

const dispositivo = () => ({ nombre: nombreDispositivo(), plataforma: 'web' as const });

export interface RespuestaCodigo {
  enviado: true;
  expira_en: string;
  codigo_desarrollo?: string;
}

export const pedirCodigo = (telefono: string) =>
  api<RespuestaCodigo>(`/tienda/publico/${SLUG}/codigo`, { cuerpo: { telefono }, sinSesion: true });

function abrirSesion(r: RespuestaEntrada): void {
  const previa = leer<Perfil>(CLAVE_PERFIL);
  // Otra persona en el mismo celular: no se le muestra el perfil ni el negocio de la anterior.
  if (previa && !mismaCuenta(previa, r)) {
    borrar(CLAVE_PERFIL);
    borrar(CLAVE_NEGOCIO);
    useCuenta.setState({ perfil: null, negocioId: null, estado: 'sin_cargar' });
  }
  const s: Sesion = {
    access_token: r.access_token,
    refresh_token: r.refresh_token,
    expira_en: r.expira_en,
    dispositivo_id: r.dispositivo_id,
    telefono: r.telefono,
    identidad_id: r.identidad_id ?? null,
    cuenta: r.cuenta ?? null,
    google: r.google ?? null,
  };
  fijarSesion(s);
}

// ---------- El preventista (decisión D60) ----------

interface RespuestaVendedor extends Tokens {
  usuario: { id: string; nombre: string; email: string | null };
}

/**
 * El preventista entra con el correo y la contraseña de su usuario del personal: la tienda queda en modo vendedor, con
 * su cartera como negocios. Otra persona antes en el mismo celular: no se le muestra lo de la anterior.
 */
export async function entrarComoVendedor(email: string, password: string): Promise<void> {
  const r = await api<RespuestaVendedor>(`/tienda/publico/${SLUG}/vendedor/entrar`, {
    cuerpo: {
      email,
      password,
      dispositivo: { ...dispositivo(), nombre: nombreDispositivo().replace('Tienda', 'Preventa') },
    },
    sinSesion: true,
  });
  const vendedor = { id: r.usuario.id, nombre: r.usuario.nombre, email: r.usuario.email };
  const previa = leer<Perfil>(CLAVE_PERFIL);
  if (!previa || !mismaCuenta(previa, { telefono: null, vendedor })) {
    borrar(CLAVE_PERFIL);
    borrar(CLAVE_NEGOCIO);
    useCuenta.setState({ perfil: null, negocioId: null, estado: 'sin_cargar' });
  }
  fijarSesion({
    access_token: r.access_token,
    refresh_token: r.refresh_token,
    expira_en: r.expira_en,
    dispositivo_id: r.dispositivo_id,
    telefono: null,
    identidad_id: null,
    vendedor,
  });
}

// ---------- Correo y contraseña (decisión D51) ----------

/** Entra con el correo y la contraseña. Una cuenta todavía sin negocio vuelve con `estado: 'falta_completar'`. */
export async function entrarConCorreo(email: string, password: string): Promise<RespuestaCuenta> {
  const r = await api<RespuestaCuenta>(`/tienda/publico/${SLUG}/entrar`, {
    cuerpo: { email, password, dispositivo: dispositivo() },
    sinSesion: true,
  });
  abrirSesion(r);
  return r;
}

/** "Creá tu cuenta": el correo y la contraseña. Después completa los datos del negocio. */
export async function crearCuenta(email: string, password: string): Promise<RespuestaCuenta> {
  const r = await api<RespuestaCuenta>(`/tienda/publico/${SLUG}/cuenta`, {
    cuerpo: { email, password, dispositivo: dispositivo() },
    sinSesion: true,
  });
  abrirSesion(r);
  return r;
}

export interface RespuestaOlvide {
  enviado: true;
  /** correo: llega por correo. pedir: el servidor no manda correos (se lo pide a la distribuidora). */
  modo: 'correo' | 'pedir';
  /** Solo en la demostración: el enlace, para mostrarlo en pantalla. */
  enlace_demostracion?: string;
}

export const pedirEnlaceClave = (email: string) =>
  api<RespuestaOlvide>(`/tienda/publico/${SLUG}/olvide`, { cuerpo: { email }, sinSesion: true });

export interface DatosEnlaceClave {
  email: string | null;
  nombre: string | null;
  /** Todavía no tenía contraseña (la crea por primera vez). */
  nueva: boolean;
  vence: string;
}

export const revisarEnlaceClave = (token: string) =>
  api<DatosEnlaceClave>(`/tienda/publico/${SLUG}/clave/revisar`, { cuerpo: { token }, sinSesion: true });

/** Elige la contraseña con el enlace de un solo uso y entra. */
export async function elegirClave(token: string, password: string): Promise<RespuestaCuenta> {
  const r = await api<RespuestaCuenta>(`/tienda/publico/${SLUG}/clave`, {
    cuerpo: { token, password, dispositivo: dispositivo() },
    sinSesion: true,
  });
  abrirSesion(r);
  return r;
}

/** Entra con el código. Un número nuevo (registro abierto) vuelve con `estado: 'falta_completar'` (decisión D48). */
export async function entrarConCodigo(telefono: string, codigo: string): Promise<RespuestaEntrada> {
  const r = await api<RespuestaEntrada>(`/tienda/publico/${SLUG}/verificar`, {
    cuerpo: { telefono, codigo, dispositivo: dispositivo() },
    sinSesion: true,
  });
  abrirSesion(r);
  return r;
}

export async function entrarConEnlace(token: string): Promise<void> {
  abrirSesion(
    await api<RespuestaEntrada>(`/tienda/publico/${SLUG}/enlace`, {
      cuerpo: { token, dispositivo: dispositivo() },
      sinSesion: true,
    }),
  );
}

// ---------- Google, Apple y Facebook (decisiones D48 y D51) ----------

const proveedorVencido = (p: Proveedor) =>
  new ErrorApi(
    401,
    'GOOGLE_VENCIDO',
    `El ingreso con ${NOMBRE_PROVEEDOR[p]} venció o se abrió en otro navegador. Tocá "Continuar con ${NOMBRE_PROVEEDOR[p]}" de nuevo.`,
  );

/** Canjea el código de la vuelta del proveedor por la sesión. La primera vez el estado es "falta_completar". */
export async function entrarConProveedor(p: Proveedor, codigo: string): Promise<RespuestaGoogle> {
  const secreto = tomarSecreto();
  if (!secreto) throw proveedorVencido(p);
  const r = await api<RespuestaGoogle>(`/tienda/publico/${SLUG}/${p}`, {
    cuerpo: { codigo, secreto, dispositivo: dispositivo() },
    sinSesion: true,
  });
  abrirSesion(r);
  return r;
}

export interface DatosRegistro {
  nombre: string;
  negocio: string;
  /** Celular o WhatsApp del negocio: solo un dato de contacto (no se verifica). */
  telefono: string;
  /** Solo si la cuenta no tiene correo (Facebook puede no darlo). */
  email?: string;
  documento: string | null;
  /** La razón social de la DNIT, si la eligió (decisión D61). */
  razon_social?: string;
  direccion: string;
  referencia: string | null;
  ubicacion: { lat: number; lng: number; precision_m: number | null; origen: 'gps' | 'mapa' } | null;
}

/** "Completá tus datos": crea el negocio y queda el perfil listo para pedir. */
export async function registrar(datos: DatosRegistro): Promise<Perfil> {
  const p = await api<Perfil>('/tienda/registro', { cuerpo: datos, timeoutMs: 30_000 });
  guardar(CLAVE_PERFIL, p);
  const negocioId = p.clientes[0]?.id ?? null;
  if (negocioId) guardar(CLAVE_NEGOCIO, negocioId);
  useCuenta.setState({ perfil: p, estado: 'listo', mensaje: null, sinRed: false, negocioId });
  abrirCarrito(negocioId);
  return p;
}

/** Vincular Google, Apple o Facebook a la cuenta desde "Tu cuenta": va al proveedor y vuelve a Perfil. */
export async function vincularProveedor(p: Proveedor, clienteId: string): Promise<void> {
  const desafio = await nuevoDesafio();
  const r = await api<{ url: string }>('/tienda/vincular/iniciar', {
    cuerpo: { proveedor: p, cliente_id: clienteId, desafio },
  });
  location.assign(r.url);
}

/** Termina de vincular con el código de la vuelta. */
export async function terminarVinculo(p: Proveedor, codigo: string): Promise<{ email: string | null }> {
  const secreto = tomarSecreto();
  if (!secreto) throw proveedorVencido(p);
  const r = await api<{ email: string | null }>('/tienda/vincular', { cuerpo: { codigo, secreto } });
  void cargarPerfil();
  return r;
}

/** Cierra la sesión. El carrito queda guardado en el celular para cuando vuelva a entrar. */
export async function salir(): Promise<void> {
  const vendedor = !!useSesion.getState().sesion?.vendedor;
  try {
    // El preventista cierra su sesión del personal (decisión D60).
    await api(vendedor ? '/auth/salir' : '/tienda/salir', { metodo: 'POST', timeoutMs: 6000 });
  } catch {
    /* sin señal o ya vencida: igual se cierra acá */
  }
  // El preventista elige el cliente en cada visita: al salir no queda ninguno elegido.
  if (vendedor) {
    borrar(CLAVE_NEGOCIO);
    useCuenta.setState({ negocioId: null });
  }
  borrar(CLAVE_PERFIL);
  useCuenta.setState({ perfil: null, estado: 'sin_cargar', mensaje: null, sinRed: false });
  abrirCarrito(null);
  fijarSesion(null, 'salio');
}

/** Lee el enlace de WhatsApp (`#acceso=<token>`) y lo borra de la dirección para que no quede en el historial. */
export function tomarEnlaceDeAcceso(): string | null {
  if (typeof location === 'undefined') return null;
  const m = /(?:^#|&)acceso=([A-Za-z0-9_-]{20,80})/.exec(location.hash);
  if (!m) return null;
  history.replaceState(history.state, '', `${location.pathname}${location.search}`);
  return m[1]!;
}

// Con el perfil guardado se abre el carrito del negocio enseguida, sin esperar a la API.
if (inicial) abrirCarrito(negocioDe(inicial, useCuenta.getState().negocioId)?.id ?? null);
